import {IS_SINGLE_PLAYER_VALUE} from "data/constants";
import {Bindings} from "data/constants/bindings";
import {RequestState, RoundType} from "data/enums";
import {ILineup, IPickResponse} from "data/providers/api/pinpoint.api.provider";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPinpointStore} from "data/stores/pinpoint/pinpoint.store";
import type {IFilter, IPlayerPoolStore} from "data/stores/player_pool/player_pool.store";
import type {IPlayer, IPlayersStore} from "data/stores/players/players.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {chain} from "lodash";
import {action, computed, makeAutoObservable, observable} from "mobx";
import {ChangeEvent, SyntheticEvent} from "react";
import {IPlayerPoolProps} from "views/components/player_pool/pool/player_pool.component";

interface IFilterForm extends HTMLFormElement {
	search: HTMLInputElement;
}

type IPoolControllerProps = IPlayerPoolProps & {
	roundId: number | null;
};

export interface IPoolController extends ViewController<IPoolControllerProps> {
	get i18n(): ILocalizationStore;

	get filter(): IFilter;

	get playersList(): IPlayer[];
	get allLineup(): number[];
	get selectedPlayerId(): number[];
	get onConfirmDisabled(): boolean;
	get currentPick(): IPickResponse | null;
	get isSinglePlayer(): boolean;

	onSelectedPlayerId: (ids: number[]) => void;
	onPlayerSelect: (event: ChangeEvent<HTMLInputElement>) => void;
	onConfirm: () => void;

	onSetType(type: RoundType): void;
	handleFormOnChange: (event: ChangeEvent<IFilterForm>) => void;
	handleFormSubmit: (event: SyntheticEvent<IFilterForm>) => void;
}

@injectable()
export class PoolController implements IPoolController {
	@observable _requestState: RequestState = RequestState.IDLE;

	@observable _roundId?: number | null;
	@observable _selectedPlayerId: number[] = [];
	@observable _type: RoundType = RoundType.LONGESTDRIVE;

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PlayerPoolStore) private _poolStore: IPlayerPoolStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.PinpointStore) private _pinpointStore: IPinpointStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get filter() {
		return this._poolStore.filter;
	}

	get round() {
		if (!this._roundId) {
			return null;
		}

		return this._roundStore.getRoundById(this._roundId);
	}

	get isSinglePlayer() {
		return Boolean(this.round?.name.toLowerCase().includes(IS_SINGLE_PLAYER_VALUE));
	}

	get golfers() {
		return this.round?.golfers || [];
	}

	get golfersPairs() {
		return this.golfers.reduce((acc, el) => {
			acc.push([...(el.usa ?? [])]);
			acc.push([...(el.europe ?? [])]);
			return acc;
		}, [] as number[][]);
	}

	@computed get playersList() {
		const playersList = this._playerStore.availablePlayersList.map((it) => {
			const pairs = this.golfersPairs.find((el) => el.includes(it.id)) || [];
			const notPlaying = this.golfers.length > 0 && pairs.length === 0;

			return {
				...it,
				pairs,
				notPlaying,
			};
		});

		if (this.isSinglePlayer) {
			return chain(playersList)
				.filter(this.isListed)
				.orderBy(["notPlaying", "worldRanking"], ["asc", "asc"])
				.value();
		}

		return chain(playersList)
			.filter(this.isListed)
			.uniqBy("pairs")
			.orderBy(["notPlaying", "worldRanking"], ["asc", "asc"])
			.value();
	}

	get allLineup() {
		return [];
	}

	get onConfirmDisabled() {
		return !this._selectedPlayerId.length;
	}

	get selectedPlayerId() {
		return this._selectedPlayerId;
	}

	get currentPick() {
		return this._pinpointStore.getPickByRoundId(this._roundId!);
	}

	@action onSetType = (type: RoundType) => {
		this._type = type;
	};

	@action onSelectedPlayerId = (ids: number[]) => {
		this._selectedPlayerId = ids;
	};

	@action onPlayerSelect = (event: ChangeEvent<HTMLInputElement>) => {
		if (!event.target.value) {
			this._selectedPlayerId = [];
			return;
		}

		const playerId = Number(event.target.value);
		this._selectedPlayerId = [playerId];
	};

	@action onConfirm = () => {
		const updatedPick = {
			roundId: this._roundId!,
			...this.currentPick,
			...(this._type === RoundType.LONGESTDRIVE && {
				longestDriveLineup: {
					...(this.currentPick?.longestDriveLineup || {}),
					golfers: this._selectedPlayerId,
				} as ILineup,
			}),
			...(this._type === RoundType.NEARESTPIN && {
				nearestPinLineup: {
					...(this.currentPick?.nearestPinLineup || {}),
					golfers: this._selectedPlayerId,
				} as ILineup,
			}),
		};

		this._pinpointStore.updatePickByRoundId(this._roundId!, updatedPick);

		this._selectedPlayerId = [];
		this._poolStore.closePoolModal();
	};

	isListed = (player: IPlayer) => {
		const {search} = this.filter;
		return !search || player.fullName.toLowerCase().includes(search.toLowerCase());
	};

	@action handleFormOnChange = (event: ChangeEvent<IFilterForm>) => {
		const {search} = event.currentTarget;
		this.filter.search = search.value;
	};

	@action handleFormSubmit = (event: SyntheticEvent<IFilterForm>) => {
		event.preventDefault();
	};

	dispose() {
		return;
	}

	init({roundId, type}: IPoolControllerProps) {
		this._roundId = roundId;
		this._type = type;
	}
}
